import { AppRoutes } from '@/app/routes';

/**
 * The sidebar is rendered twice (desktop column and phone drawer): the first match of a selector
 * is often the hidden copy, so a spotlight must look for a copy the user can actually see.
 */
export function isElementVisible(element: Element): boolean {
  if (element.closest('[aria-hidden="true"]')) return false;
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;
  // A closed drawer is translated off screen sideways; above or below can still be scrolled to.
  if (rect.right <= 0 || rect.left >= window.innerWidth) return false;
  return window.getComputedStyle(element).visibility !== 'hidden';
}

export function findVisibleTarget(selector: string): Element | null {
  return Array.from(document.querySelectorAll(selector)).find(isElementVisible) ?? null;
}

function isBottomTabBar(nav: Element | null): boolean {
  if (!nav || window.getComputedStyle(nav).position !== 'fixed') return false;
  return nav.getBoundingClientRect().bottom >= window.innerHeight - 1;
}

function findBottomTabBar(): HTMLElement | null {
  return (
    Array.from(document.querySelectorAll<HTMLElement>('nav')).find(
      (nav) => isBottomTabBar(nav) && isElementVisible(nav),
    ) ?? null
  );
}

/**
 * Native app on a phone: Stats, Video, Teams… are tabs at the bottom, so a step about a sidebar
 * entry points at its tab instead of opening the "Plus" drawer. Matched on the link target.
 */
export function findTabBarEquivalent(selector: string): HTMLElement | null {
  const hrefs = new Set(
    Array.from(document.querySelectorAll(selector))
      .map((element) => element.getAttribute('href'))
      .filter((href): href is string => Boolean(href)),
  );
  if (hrefs.size === 0) return null;
  const bar = findBottomTabBar();
  if (!bar) return null;
  return (
    Array.from(bar.querySelectorAll<HTMLAnchorElement>('a[href]')).find((link) => {
      const href = link.getAttribute('href');
      return href !== null && hrefs.has(href) && isElementVisible(link);
    }) ?? null
  );
}

export interface AppShellTarget {
  element: Element;
  /** In the fixed tab bar: never scrolled to. */
  inTabBar: boolean;
}

/**
 * Native app on a phone, where the tab bar and the « Plus » screen replace the side menu
 * (VOL-343): a menu step points at the whole bar, the entry's tab, its row on the « Plus »
 * screen when open, else the « Plus » tab. Null without a tab bar, or before it is drawn.
 */
export function findAppShellTarget(selector: string): AppShellTarget | null {
  const bar = findBottomTabBar();
  if (!bar) return null;
  if (selector === '[data-onboarding="sidebar"]') return { element: bar, inTabBar: true };
  const tab = findTabBarEquivalent(selector);
  if (tab) return { element: tab, inTabBar: true };
  const row = findVisibleTarget(selector);
  if (row) return { element: row, inTabBar: false };
  const more = bar.querySelector(`a[href="${AppRoutes.nativeApp.more}"]`);
  return more ? { element: more, inTabBar: true } : null;
}
